import { useState } from 'react';
import { Alert, AlertDescription, AlertTitle, Skeleton } from '@noor/ui';
import { cn } from '@noor/lib';
import { ModerationDecisionButtons } from '@/features/moderation-actions';
import {
  applyRequestChanges,
  payloadEntityOf,
  requestBaselineOf,
  requestChangedKeys,
  type ModerationEntity,
  type ModerationEntityType,
  type ModerationPendingSlot,
  type ModerationVersion,
  type Translate,
} from '@/entities/moderation';
import type { ModerationReview } from '../../model/use-moderation-review';
import { PendingModifierGroupsSection } from './pending-modifier-groups-section';
import { RequestMeta } from './request-meta';
import { ReviewAlerts } from './review-alerts';
import { decisionLabels, reviewTitle } from './review-copy';
import { ReviewGate } from './review-gate';
import { ReviewModalShell } from './review-modal-shell';
import { VersionColumn } from './version-column';

/** One proposal column of the compare grid. */
interface ProposalColumn {
  slot: ModerationPendingSlot;
  requestId: string;
  entity: ModerationEntity;
  version: ModerationVersion | null;
  submittedAt: string | null | undefined;
  /** The request the reviewer opened. */
  isOwn: boolean;
  changedKeys: string[];
}

interface UpdateReviewModalProps {
  entityType: ModerationEntityType;
  review: ModerationReview;
  t: Translate;
  onClose: () => void;
}

/**
 * Review of a PENDING edit (Figma 584:11839): the live version beside every
 * proposal waiting on the entity. One source pending → two columns (live |
 * proposal) and a single decision row. Both sources pending → three columns
 * (live | merchant | sync), one decision row per proposal, and a note that
 * approving one withdraws the other (the card holds one request per source,
 * and approving either deletes its sibling).
 */
export function UpdateReviewModal({
  entityType,
  review,
  t,
  onClose,
}: UpdateReviewModalProps) {
  const [reviewConfirmed, setReviewConfirmed] = useState(false);
  const [showUnchanged, setShowUnchanged] = useState(true);
  const { request, operation } = review;
  const labels = decisionLabels(operation, t);
  const loading = review.cardStatus === 'loading';

  const live = review.live;
  const columns = proposalColumns(review);
  const compareKeys = [
    ...new Set([
      ...Object.keys(live ?? {}),
      ...columns.flatMap((column) => Object.keys(column.entity)),
    ]),
  ].sort();
  const competing = columns.length > 1;
  const changedUnion = new Set(columns.flatMap((column) => column.changedKeys));
  const toggleUnchanged = () => setShowUnchanged((shown) => !shown);

  return (
    <ReviewModalShell
      width={competing ? 'compare-wide' : 'compare'}
      title={reviewTitle(entityType, operation, t)}
      meta={<RequestMeta request={request} operation={operation} t={t} />}
      banners={
        <>
          <ReviewAlerts review={review} t={t} />
          {competing && (
            <Alert variant="info" data-testid="moderation-competing-note">
              <AlertTitle>
                {t('review.competingTitle', 'Две версии ждут решения')}
              </AlertTitle>
              <AlertDescription>
                <p>
                  {t(
                    'review.competingHint',
                    'Изменения пришли и от мерчанта, и из синхронизации. Одобрение одной версии автоматически отзывает другую.',
                  )}
                </p>
              </AlertDescription>
            </Alert>
          )}
        </>
      }
      footer={
        <>
          <ReviewGate confirmed={reviewConfirmed} onChange={setReviewConfirmed} />
          {competing ? (
            columns.map((column) => (
              <div
                key={column.requestId}
                className="grid grid-cols-1 items-center gap-2 sm:grid-cols-[minmax(0,1fr)_minmax(0,2fr)]"
                data-testid={`moderation-decision-${column.slot}`}
              >
                <p className="min-w-0 text-sm font-medium text-foreground">
                  {slotTitle(column.slot, t)}
                  {column.isOwn && (
                    <span className="ml-2 text-xs font-normal text-primary">
                      {t('review.thisRequest', 'эта заявка')}
                    </span>
                  )}
                </p>
                <ModerationDecisionButtons
                  entityType={entityType}
                  changeRequestId={column.requestId}
                  reviewConfirmed={reviewConfirmed}
                  approveLabel={labels.approve}
                  rejectLabel={labels.reject}
                  onDone={onClose}
                />
              </div>
            ))
          ) : (
            <ModerationDecisionButtons
              entityType={entityType}
              changeRequestId={columns[0]?.requestId ?? request.id}
              reviewConfirmed={reviewConfirmed}
              approveLabel={labels.approve}
              rejectLabel={labels.reject}
              onDone={onClose}
            />
          )}
        </>
      }
      onClose={onClose}
    >
      {loading ? (
        <div className="grid grid-cols-1 gap-[var(--form-gap-x)] sm:grid-cols-2">
          <Skeleton className="h-96 w-full rounded-[20px]" />
          <Skeleton className="h-96 w-full rounded-[20px]" />
        </div>
      ) : (
        <div
          className={cn(
            'grid grid-cols-1 gap-x-[var(--form-gap-x)] gap-y-[var(--form-gap-y)]',
            competing ? 'lg:grid-cols-3' : 'sm:grid-cols-2',
          )}
        >
          <VersionColumn
            variant="live"
            title={t('detail.currentVersion', 'Текущая версия')}
            timestamp={live?.updated_at ?? request.baseline_updated_at}
            entityType={entityType}
            own={live}
            other={null}
            compareKeys={compareKeys}
            highlightKeys={new Set()}
            changedUnion={changedUnion}
            showUnchanged={showUnchanged}
            onToggleUnchanged={toggleUnchanged}
            version={review.card?.prod}
            emptyLabel={t('detail.noLiveVersion', 'Текущая версия недоступна.')}
            t={t}
          />
          {columns.map((column) => (
            <VersionColumn
              key={column.requestId}
              variant="pending"
              selected={competing && column.isOwn}
              title={slotTitle(column.slot, t)}
              timestamp={column.submittedAt ?? request.created_at}
              timestampLabel={
                column.slot === 'manual'
                  ? t('review.submittedAt', 'Подано: {{date}}')
                  : undefined
              }
              badges={
                column.isOwn && competing ? (
                  <span className="rounded-full bg-primary/10 px-2 py-1 text-xs font-medium text-primary">
                    {t('review.thisRequest', 'эта заявка')}
                  </span>
                ) : undefined
              }
              entityType={entityType}
              own={column.entity}
              other={live}
              compareKeys={compareKeys}
              highlightKeys={new Set(column.changedKeys)}
              changedUnion={changedUnion}
              showUnchanged={showUnchanged}
              onToggleUnchanged={toggleUnchanged}
              version={column.version}
              liveVersion={review.card?.prod}
              emptyLabel={t('detail.noPendingVersion', 'Нет предложенных данных.')}
              t={t}
            />
          ))}
        </div>
      )}
      {entityType === 'products' && review.card && (
        <PendingModifierGroupsSection
          entityType={entityType}
          groups={review.card.modifier_groups}
          t={t}
        />
      )}
    </ReviewModalShell>
  );
}

function slotTitle(slot: ModerationPendingSlot, t: Translate): string {
  return slot === 'sync'
    ? t('review.slot.sync', 'Версия синхронизации')
    : t('review.slot.manual', 'Версия мерчанта');
}

/**
 * The proposals to compare: every pending slot of the card, plus — when the
 * card does not carry this request (card failed, or moved on) — this
 * request's own proposal built from its payload, so it is always reviewable.
 */
function proposalColumns(review: ModerationReview): ProposalColumn[] {
  const { request } = review;
  const columns: ProposalColumn[] = review.pendingSlots.map(({ slot, version }) => {
    const cardEntity = version.entity ?? {};
    const slotRequest = version.change_request ?? (slot === 'manual' ? request : null);
    const entity = applyRequestChanges(
      slot === 'manual'
        ? {
            ...(requestBaselineOf(request) ?? {}),
            ...payloadEntityOf(request),
            ...cardEntity,
          }
        : cardEntity,
      slotRequest,
    );
    return {
      slot,
      requestId: version.change_request?.id ?? request.id,
      entity,
      version,
      submittedAt: version.change_request?.created_at ?? entity.updated_at,
      isOwn: review.ownSlot?.slot === slot,
      changedKeys: requestChangedKeys(
        version.change_request ?? (review.ownSlot?.slot === slot ? request : null),
        version.changed_fields,
      ),
    };
  });
  if (review.ownSlot) return columns;

  const source = (request.source ?? '').trim().toLowerCase();
  const slot: ModerationPendingSlot = source === 'sync' ? 'sync' : 'manual';
  const own: ProposalColumn = {
    slot,
    requestId: request.id,
    entity: review.proposed,
    version: null,
    submittedAt: request.created_at,
    isOwn: true,
    changedKeys: requestChangedKeys(request),
  };
  return [...columns, own].sort((a, b) => slotOrder(a.slot) - slotOrder(b.slot));
}

function slotOrder(slot: ModerationPendingSlot): number {
  return slot === 'manual' ? 0 : 1;
}
